// useCalendar — events + view state for the Calendar section.
//
// Phase 1: personal events only. We fetch a wide range (the visible
// month padded to its 6×7 grid) so all three views (Week/Day/Month)
// share the same in-memory dataset and switching views never refetches.
// Range is keyed on `anchorDate` — moving forward/back a week or month
// shifts the anchor and re-fetches.

import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import { api, isOfflineError, peekCache, type CalendarCreateArgs, type CalendarEvent, type CalendarUpdatePatch } from "./rpc";

export type CalendarViewMode = "week" | "day" | "month";

export interface UseCalendar {
  events: CalendarEvent[];
  loading: boolean;
  error: string | null;
  anchor: Date;
  setAnchor: (d: Date) => void;
  view: CalendarViewMode;
  setView: (v: CalendarViewMode) => void;
  focusDate: Date;
  setFocusDate: (d: Date) => void;
  refresh: () => Promise<void>;
  createEvent: (args: CalendarCreateArgs) => Promise<CalendarEvent | null>;
  updateEvent: (id: string, patch: CalendarUpdatePatch) => Promise<CalendarEvent | null>;
  deleteEvent: (id: string) => Promise<boolean>;
}

export function useCalendar(): UseCalendar {
  const [anchor, setAnchorState] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [view, setView] = useState<CalendarViewMode>("week");
  const [focusDate, setFocusDate] = useState<Date>(() => new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Range covers the anchor's month padded a week on each side so the
  // 6×7 month grid always has data, and Week view (which may straddle
  // a month boundary) is also covered.
  const range = useMemo(() => {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    start.setDate(start.getDate() - 14);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    end.setDate(end.getDate() + 14);
    end.setHours(23, 59, 59, 999);
    return { rangeStart: start.toISOString(), rangeEnd: end.toISOString() };
  }, [anchor]);

  const refresh = useCallback(async () => {
    setError(null);

    // SWR: paint cached events for this range first, then refresh. The
    // cache is keyed on (rangeStart, rangeEnd) — when the user pages
    // back to a previously-visited month, it shows instantly.
    let hadCache = false;
    try {
      const cached = await peekCache<{ ok: true; events: CalendarEvent[] }>("calendar.listEvents", range);
      if (cached?.events) {
        hadCache = true;
        setEvents(cached.events);
        setLoading(false);
      }
    } catch { /* swallow */ }
    if (!hadCache) setLoading(true);

    try {
      const list = await api.calendarListEvents(range);
      setEvents(list);
    } catch (e: any) {
      if (!hadCache || !isOfflineError(e)) {
        setError(e?.message || "failed to load events");
      }
    } finally {
      setLoading(false);
    }
  }, [range.rangeStart, range.rangeEnd]);

  useEffect(() => { refresh(); }, [refresh]);

  const setAnchor = useCallback((d: Date) => {
    const clean = new Date(d);
    clean.setHours(0, 0, 0, 0);
    setAnchorState(clean);
  }, []);

  const createEvent = useCallback(async (args: CalendarCreateArgs) => {
    try {
      const evt = await api.calendarCreateEvent(args);
      // Recurring events: the server returns just the master row, but the
      // calendar's in-memory `events` list holds EXPANDED instances (one
      // per occurrence within the range). An optimistic single-row append
      // would only show the first occurrence until a manual refresh. For
      // these, ask the server to re-expand the range.
      if (evt.rrule) {
        await refresh();
      } else {
        setEvents((prev) => [...prev, evt]);
      }
      return evt;
    } catch (e: any) {
      setError(e?.message || "failed to create event");
      return null;
    }
  }, [refresh]);

  const updateEvent = useCallback(async (id: string, patch: CalendarUpdatePatch) => {
    try {
      // Snapshot whether the event WAS recurring BEFORE the patch — if
      // we're removing recurrence (rrule cleared) the cache still holds
      // all the stale instance expansions and an in-memory .map by id
      // would leave 19 ghosts behind. Refresh in either direction
      // (becoming recurring OR un-becoming) for correctness.
      const wasRecurring = events.some((e) => (e.masterId || e.id) === id && (e.isRecurring || !!e.rrule));
      const evt = await api.calendarUpdateEvent({ id, patch });
      const nowRecurring = !!evt.rrule;
      if (wasRecurring || nowRecurring || patch.rrule !== undefined || patch.dtstart !== undefined || patch.dtend !== undefined) {
        await refresh();
      } else {
        setEvents((prev) => prev.map((e) => (e.id === id ? evt : e)));
      }
      return evt;
    } catch (e: any) {
      setError(e?.message || "failed to update event");
      return null;
    }
  }, [refresh, events]);

  const deleteEvent = useCallback(async (id: string) => {
    try {
      await api.calendarDeleteEvent(id);
      // Delete may be a recurring master — its expanded instances live in
      // the events array under synthetic ids referencing masterId=id.
      // Strip both the master and every expansion in one pass.
      setEvents((prev) => prev.filter((e) => e.id !== id && e.masterId !== id));
      return true;
    } catch (e: any) {
      setError(e?.message || "failed to delete event");
      return false;
    }
  }, []);

  return {
    events, loading, error,
    anchor, setAnchor, view, setView,
    focusDate, setFocusDate,
    refresh, createEvent, updateEvent, deleteEvent,
  };
}
